import type { FoodPackOffer, Quota } from "../types";

/**
 * Счётчик распознаваний словами для человека: когда начнётся следующий день сервера — по его часам
 * («сегодня в 19:00», «завтра в 3:00»), до какого дня идёт пакет, цена пакета.
 */

const intlLang = (lang: string) => (lang === "cn" ? "zh" : lang || "ru");

/** Счётчик дня (сервер с 03.11.2026); без period — прежний счётчик месяца. */
export const isDaily = (q: Quota | null | undefined): boolean => q?.period === "day";

/**
 * Когда счётчик дня обнулится (Date): resets_at сервера, а у ответа без него — полночь даты resets
 * в часовом поясе сервера (UTC; «Europe/Moscow» — +03:00, без перехода на летнее время).
 */
export const resetAt = (q: Quota | null | undefined): Date | null => {
    if (!q) return null;
    const iso =
        q.resetsAt ||
        (/^\d{4}-\d{2}-\d{2}$/.test(q.resets) ? `${q.resets}T00:00:00${q.tz === "Europe/Moscow" ? "+03:00" : "Z"}` : q.resets);
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
};

/**
 * До какого момента модель сегодня не зовётся после 429 daily_calls: конец дня сервера из счётчика дня (resets_at),
 * а без него — ближайшая полночь UTC (день сервера .com, food_quota.next_reset). Retry-After того же ответа браузер
 * не видит: сайт и сервер на разных адресах, а сервер этот заголовок не открывает (CORS expose_headers).
 */
export const callsResetAt = (q: Quota | null | undefined, now: number = Date.now()): Date => {
    const at = isDaily(q) ? resetAt(q) : null;
    if (at && at.getTime() > now) return at;
    const d = new Date(now);
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1));
};

const localYmd = (d: Date) => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;

export type ResetWhen =
    | { kind: "today" | "tomorrow"; time: string }
    | { kind: "date"; date: string; time: string };

/** Время сброса по часам человека: сегодня / завтра / дата, и время «3:00». */
export const resetWhen = (at: Date, lang: string, now: Date = new Date()): ResetWhen => {
    let time: string;
    try {
        time = new Intl.DateTimeFormat(intlLang(lang), { hour: "numeric", minute: "2-digit" }).format(at);
    } catch {
        time = `${at.getHours()}:${String(at.getMinutes()).padStart(2, "0")}`;
    }
    if (localYmd(at) === localYmd(now)) return { kind: "today", time };
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    if (localYmd(at) === localYmd(tomorrow)) return { kind: "tomorrow", time };
    return { kind: "date", date: dayMonth(at, lang), time };
};

/** «3 декабря» по часам человека. */
export const dayMonth = (d: Date, lang: string): string => {
    try {
        return new Intl.DateTimeFormat(intlLang(lang), { day: "numeric", month: "long" }).format(d);
    } catch {
        return d.toLocaleDateString();
    }
};

/** До какого дня идёт пакет («3 декабря»), или "" — пакета нет. */
export const packUntil = (q: Quota | null | undefined, lang: string): string => {
    if (!q?.pack?.active || !q.pack.until) return "";
    const d = new Date(q.pack.until);
    return isNaN(d.getTime()) ? "" : dayMonth(d, lang);
};

/** Цена пакета: «$9.90» (как у пакетов поиска, supPlan fmtPrice), «990 ₽». */
export const offerPrice = (o: FoodPackOffer): string => {
    if (o.currency === "USD") return `$${o.price.toFixed(2)}`;
    if (o.currency === "RUB") return `${Math.round(o.price)} ₽`;
    return `${o.price} ${o.currency}`;
};
